import { useMutation } from '@apollo/client';
import { UPDATE_CARD } from '@mutations/updateCard';
import { GET_CUSTOMER } from '@queries/getCustomer';
import { BaseText, Button, FieldInput, Meta, theme, Toggle } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import Icon from 'components/icons/Icon';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useCreditCardCount from 'hooks/useCreditCardCount';
import useDebounce from 'hooks/useDebounce';
import useSetDefaultTokenizedPaymentOption from 'hooks/useSetDefaultTokenizedPaymentOption';
import useShowPaymentCardStatus from 'hooks/useShowPaymentCardStatus';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaymentStore } from 'stores/paymentStore';
import styled from 'styled-components/native';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  CardStatus,
  Mutation,
  MutationUpdateCardArgs,
} from 'types/generatedSchemaTypes';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { getCardExpiryDateText } from 'utils/paymentUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { useUiStore } from 'stores/uiStore';

export const Row = styled.View`
  flex-direction: row;
  align-items: flex-start;
`;

const RowOption = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  padding-top: 16px;
`;

export const IconWrapper = styled.View`
  margin-right: 4px;
`;

export const TextWrapper = styled.View`
  flex-shrink: 1;
  align-self: center;
`;

const Container = styled.View`
  flex: 1;
  flex-direction: column;
  flex-basis: auto;
`;

const Grow = styled.View`
  flex: 1 0 auto;
`;

const Footer = styled.View`
  flex-direction: column;
  justify-content: flex-end;
  flex-shrink: 0;
`;

type PaymentCardContentProps = {
  handleDone?: () => void;
  isSheet?: boolean;
};

const PaymentCardContent = ({
  handleDone,
  isSheet = false,
}: PaymentCardContentProps) => {
  const { t } = useTranslation();
  const {
    status,
    category,
    isDefault,
    description,
    cardId,
    newCard,
    expMonth,
    expYear,
  } = usePaymentStore.getState().selectedPaymentCard;
  const hasMultipleCards = useCreditCardCount() > 1;
  const {
    customer: { paymentCards },
  } = useContext(AccountContext);
  const [isBusiness, setIsBusiness] = useState(category === 'business');
  const [_isDefault, _setIsDefault] = useState(isDefault);
  const [name, setName] = useState(description);
  const debouncedName = useDebounce(name, 500);
  const {
    flags: { card_business, tokenized_default_payment },
  } = useContext(FeatureFlagsContext);
  const showBusinessCard = showFeatureFlag(card_business);
  const useTokenizedDefault = showFeatureFlag(tokenized_default_payment);

  const { setDefaultTokenizedPaymentOption, loading: setDefaultLoading } =
    useSetDefaultTokenizedPaymentOption();
  const { isErrorStatus, expiresSoon } = useShowPaymentCardStatus(
    (status || 'Active') as CardStatus,
    expMonth,
    expYear
  );

  const [updateCard, { loading }] = useMutation<
    Mutation,
    MutationUpdateCardArgs
  >(UPDATE_CARD, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_CUSTOMER,
        errorPolicy: 'all',
      },
    ],
    onError: () => showToast('error.paymentCardUpdate', 'error'),
  });

  const handleIsBusinessChange = async (value: boolean) => {
    setIsBusiness(value);
    updateCard({
      variables: {
        cardId: cardId,
        updateCardActionType: value
          ? 'SET_CATEGORY_AS_BUSINESS'
          : 'SET_CATEGORY_AS_UNKNOWN',
      },
    });
  };

  const handleSetDefaultToggleChange = (value: boolean) => {
    _setIsDefault(value);
  };

  const setCardAsDefault = () => {
    if (useTokenizedDefault) {
      setDefaultTokenizedPaymentOption(cardId, 'CREDIT_CARD');
    } else {
      updateCard({
        variables: {
          cardId: cardId,
          updateCardActionType: 'SET_ISDEFAULT_AS_TRUE',
        },
      });
    }
  };

  const handleSetDefaultButtonChange = () => {
    _setIsDefault(true);
    setCardAsDefault();
  };

  const handleDoneButtonClick = () => {
    if (_isDefault) {
      setCardAsDefault();
    }
    handleDone ? handleDone() : useUiStore.getState().setSheetModal('');
  };

  useEffect(() => {
    if (description !== debouncedName) {
      updateCard({
        variables: {
          cardId: cardId,
          updateCardActionType: 'SET_DESCRIPTION',
          description: debouncedName,
        },
      });
    }
  }, [cardId, debouncedName, description, updateCard]);

  const PaymentCardStatusItem = () => {
    const expiredSoonText = t(
      'paymentMethods.paymentCardStatuses.toBeExpiredOn',
      { date: getCardExpiryDateText(expMonth, expYear) }
    );
    const expiresOtherText =
      status === 'Expired' && paymentCards?.length === 1
        ? t('paymentMethods.paymentCardStatuses.expiredWithANPRNote')
        : t(`paymentMethods.paymentCardStatuses.${status.toLowerCase()}`);

    return (
      <Row testID="card-status-text">
        <IconWrapper>
          <Icon
            name={`Error-Filled${expiresSoon ? '-Black' : ''}`}
            size="m"
            color={
              expiresSoon ? theme.colors.boost.yellow : theme.colors.aimoUiRed
            }
            {...testIdentifiers(
              `credit-card-${expiresSoon ? 'warning' : 'error'}-icon`
            )}
          />
        </IconWrapper>
        <TextWrapper>
          <Meta
            color={expiresSoon ? theme.colors.black : theme.colors.aimoUiRed}
            {...testIdentifiers(
              `credit-card-${expiresSoon ? 'warning' : 'error'}-text`
            )}>
            {expiresSoon ? expiredSoonText : expiresOtherText}
          </Meta>
        </TextWrapper>
      </Row>
    );
  };

  return (
    <>
      <Container>
        <Grow>
          <StyledWrapper mb={18}>
            {(isErrorStatus || expiresSoon) && <PaymentCardStatusItem />}
            {_isDefault && (
              <Row {...testIdentifiers('card-default-text')}>
                <IconWrapper>
                  <Icon
                    name="Info-Filled"
                    size="m"
                    color={theme.colors.boost.green}
                  />
                </IconWrapper>
                <TextWrapper>
                  <Meta>{t('paymentMethods.paymentCardDefault')}</Meta>
                </TextWrapper>
              </Row>
            )}
          </StyledWrapper>
          <StyledWrapper mb={15}>
            <FieldInput
              value={name}
              title={t('paymentMethods.paymentCardName')}
              onChangeText={setName}
              testID="payment-methods-card-name"
              isSheet={isSheet}
            />
          </StyledWrapper>
          <StyledWrapper mb={18}>
            {showBusinessCard && (
              <RowOption>
                <BaseText>{t('paymentMethods.businessCardQuestion')}</BaseText>
                <Toggle
                  accessibilityLabel={t('paymentMethods.businessCardQuestion')}
                  value={isBusiness}
                  onValueChange={handleIsBusinessChange}
                  {...testIdentifiers('business-card-toggle')}
                />
              </RowOption>
            )}
            {hasMultipleCards && newCard && (
              <RowOption>
                <BaseText>{t('paymentMethods.isDefaultQuestion')}</BaseText>
                <Toggle
                  value={_isDefault}
                  onValueChange={handleSetDefaultToggleChange}
                  {...testIdentifiers('default-card-toggle')}
                />
              </RowOption>
            )}
          </StyledWrapper>
        </Grow>
      </Container>
      <StyledWrapper mb={40}>
        <Footer>
          {!_isDefault && !isErrorStatus && !newCard && (
            <StyledWrapper mb={20}>
              <Button
                loading={loading || setDefaultLoading}
                {...testIdentifiers('payment-methods-set-default-button')}
                buttonType="tertiary"
                text={t('paymentMethods.setAsDefault')}
                size="l"
                onPress={handleSetDefaultButtonChange}
              />
            </StyledWrapper>
          )}
          {!newCard && (
            <StyledWrapper mb={0}>
              <Button
                {...testIdentifiers('payment-methods-remove')}
                buttonType="tertiary"
                size="l"
                text={t('paymentMethods.removeCard')}
                onPress={() =>
                  useUiStore.getState().setSheetModal('confirmPaymentRemoval')
                }
              />
            </StyledWrapper>
          )}
          {newCard && (
            <StyledWrapper mb={0}>
              <Button
                {...testIdentifiers('payment-methods-done')}
                buttonType="secondary"
                text={t('button.done')}
                size="l"
                loading={loading || setDefaultLoading}
                onPress={handleDoneButtonClick}
              />
            </StyledWrapper>
          )}
        </Footer>
      </StyledWrapper>
    </>
  );
};

export default PaymentCardContent;
